import React from 'react';
import styled from 'styled-components';
import ControlGroup from '@splunk/react-ui/ControlGroup';
import Select, { type SelectChangeHandler } from '@splunk/react-ui/Select';
import Multiselect, {
    type MultiselectChangeHandler,
} from '@splunk/react-ui/Multiselect';
import Text, { type TextChangeHandler } from '@splunk/react-ui/Text';
import Button, { type ButtonClickHandler } from '@splunk/react-ui/Button';
import { _ } from '@splunk/ui-utils/i18n';
import Number, { type NumberChangeHandler } from '@splunk/react-ui/Number';
import CollapsiblePanel from '@splunk/react-ui/CollapsiblePanel';
import type { TabItem } from '@splunk/dashboard-types';
import { variables } from '@splunk/themes';

export interface ExportConfigArgs {
    fileType: 'pdf' | 'png';
    fileName: string;
    layoutIds: string[];
    renderTime: number;
    expandAdvancedSection?: boolean;
    isTabbedDashboard: boolean;
    onFileTypeChange: SelectChangeHandler;
    onFileNameChange: TextChangeHandler;
    onLayoutIdsChange: MultiselectChangeHandler;
    onRenderTimeChange: NumberChangeHandler;
    onResetRenderingTime: ButtonClickHandler;
    tabs: TabItem[];
}

// This is probably really brittle, but is the only way to remove "some" of the padding in the header and body for alignment
// "inset" removes all padding in the body, and there's no other way to remove the padding in the header
const StyledCollapsiblePanel = styled(CollapsiblePanel)`
    & [data-test='heading'] > button {
        padding: ${variables.spacingSmall} 0;
    }
    & [data-test='body'] > div {
        padding: ${variables.spacingMedium} 0;
    }
`;

const resetButtonStyle = {
    flexGrow: 0,
};

export const ExportConfig = ({
    fileType,
    fileName,
    layoutIds,
    renderTime,
    expandAdvancedSection = false,
    isTabbedDashboard,
    onFileTypeChange,
    onFileNameChange,
    onLayoutIdsChange,
    onRenderTimeChange,
    onResetRenderingTime,
    tabs,
}: ExportConfigArgs) => (
    <>
        <ControlGroup
            label={_('Format')}
            labelPosition="top"
            data-test="file-type"
        >
            <Select
                value={fileType}
                onChange={onFileTypeChange}
                defaultPlacement="below"
            >
                <Select.Option label={_('PDF')} value="pdf" />
                <Select.Option label={_('PNG')} value="png" />
            </Select>
        </ControlGroup>
        <ControlGroup
            label={_('File name')}
            labelPosition="top"
            data-test="file-name"
        >
            <Text value={fileName} onChange={onFileNameChange} />
        </ControlGroup>
        {isTabbedDashboard && (
            <>
                <ControlGroup
                    label={_('Tabs to download')}
                    labelPosition="top"
                    help={
                        layoutIds.length
                            ? _(
                                  'Downloading the dashboard triggers all unexecuted searches in the selected tabs.'
                              )
                            : _('At least one tab must be selected.')
                    }
                    data-test="tabs-to-download"
                >
                    <Multiselect
                        values={layoutIds}
                        onChange={onLayoutIdsChange}
                        defaultPlacement="below"
                        compact
                    >
                        {tabs.map((tab) => (
                            <Multiselect.Option
                                key={tab.layoutId}
                                value={tab.layoutId}
                                label={tab.label ?? tab.layoutId}
                            />
                        ))}
                    </Multiselect>
                </ControlGroup>
                <StyledCollapsiblePanel
                    title={_('Advanced')}
                    defaultOpen={expandAdvancedSection}
                    overflow="visible"
                    appearance="subtle"
                >
                    <ControlGroup
                        label={_('Rendering time (seconds)')}
                        labelPosition="top"
                        help={_(
                            'Time taken to render each tab before downloading. Increase this time if the exported dashboard is partially rendered.'
                        )}
                        data-test="render-time"
                    >
                        <Number
                            value={renderTime}
                            onChange={onRenderTimeChange}
                            min={0}
                        />
                        <Button
                            label={_('Reset')}
                            style={resetButtonStyle}
                            onClick={onResetRenderingTime}
                        />
                    </ControlGroup>
                </StyledCollapsiblePanel>
            </>
        )}
    </>
);
